// Pure helpers shared by the Top Albums and Favourite Albums pages.

export type AlbumSort = 'top' | 'newest' | 'oldest';

export const PAGE_SIZE = 10;
export const MAX_PAGES = 10;
export const FIRST_YEAR = 1990;

interface Dated {
  release_date?: string | null;
}

/** Years for the year dropdown, newest first: current year down to 1990. */
export function yearOptions(currentYear: number = new Date().getFullYear(), firstYear: number = FIRST_YEAR): number[] {
  const years: number[] = [];
  for (let y = currentYear; y >= firstYear; y--) years.push(y);
  return years;
}

/** Keeps albums released in `year`; 0 means any year. Albums with no known date are dropped when a year is chosen. */
export function filterAlbumsByYear<T extends Dated>(albums: T[], year: number): T[] {
  if (!year) return albums;
  const prefix = `${year}-`;
  return albums.filter(a => a.release_date?.startsWith(prefix));
}

/** 'top' keeps the source order. Date sorts put albums with no known date last in both directions. */
export function sortAlbums<T extends Dated>(albums: T[], sort: AlbumSort): T[] {
  if (sort === 'top') return albums;
  const sign = sort === 'newest' ? -1 : 1;
  return [...albums].sort((a, b) => {
    if (!a.release_date && !b.release_date) return 0;
    if (!a.release_date) return 1;
    if (!b.release_date) return -1;
    return sign * a.release_date.localeCompare(b.release_date);
  });
}

export function pageCount(total: number, pageSize: number = PAGE_SIZE, maxPages: number = MAX_PAGES): number {
  return Math.min(Math.ceil(total / pageSize), maxPages);
}

export function pageSlice<T>(items: T[], page: number, pageSize: number = PAGE_SIZE): T[] {
  return items.slice((page - 1) * pageSize, page * pageSize);
}

/** Page numbers to show, with `null` for a gap: 1 … 4 5 [6] 7 8 … 20 */
export function pageWindow(page: number, pages: number, radius: number = 2): Array<number | null> {
  const shown: Array<number | null> = [];
  let last = 0;
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || Math.abs(p - page) <= radius) {
      if (p - last === 2) shown.push(last + 1); // an ellipsis hiding a single page is pointless
      else if (p - last > 2) shown.push(null);
      shown.push(p);
      last = p;
    }
  }
  return shown;
}

const fold = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** True when every word of `query` appears in the album name or an artist name (ignoring case and accents). */
export function matchesQuery(album: { name?: string; artists?: Array<{ name: string }> }, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fold([album.name ?? '', ...(album.artists ?? []).map(a => a.name)].join(' '));
  return words.every(w => haystack.includes(w));
}

/** The set of release years present in `albums` (undated albums are ignored). */
export function releaseYears(albums: Dated[]): Set<number> {
  const years = new Set<number>();
  for (const a of albums) {
    const y = parseInt(a.release_date ?? '', 10);
    if (!isNaN(y)) years.add(y);
  }
  return years;
}
